import type { ExecutionTask, Project } from "@/engine/types";

export interface ProjectExecutionTask {
  projectId: string;
  projectName: string;
  task: ExecutionTask;
}

/**
 * Flatten execution tasks across every stored project. The project open in the editor is taken
 * from the editor (its catalog copy lags by the autosave debounce), so recent edits are visible.
 */
export function collectExecutionTasks(
  catalog: Record<string, Pick<Project, "id" | "name" | "executionTasks">>,
  open: Pick<Project, "id" | "name" | "executionTasks"> | null,
): ProjectExecutionTask[] {
  const out: ProjectExecutionTask[] = [];
  const seen = new Set<string>();
  const add = (p: Pick<Project, "id" | "name" | "executionTasks">) => {
    if (seen.has(p.id)) return;
    seen.add(p.id);
    for (const task of p.executionTasks) out.push({ projectId: p.id, projectName: p.name, task });
  };
  if (open && catalog[open.id]) add(open);
  for (const p of Object.values(catalog)) add(p);
  return out;
}

/** Tasks assigned to `name`; "" returns everything, "__unassigned" the ones with no owner. */
export function forAssignee(items: ProjectExecutionTask[], name: string): ProjectExecutionTask[] {
  if (!name) return items;
  if (name === "__unassigned") return items.filter((x) => x.task.assignees.length === 0);
  return items.filter((x) => x.task.assignees.includes(name));
}

/** All assignee names seen across projects, most frequent first. */
export function assigneeUniverse(items: ProjectExecutionTask[]): string[] {
  const count = new Map<string, number>();
  for (const { task } of items) for (const a of task.assignees) count.set(a, (count.get(a) ?? 0) + 1);
  return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "ko")).map(([n]) => n);
}
